import { FormEvent, useState } from 'react'
import { Alerta, AreaTexto, Boton, Campo, Casilla, Dialogo, Entrada, Seleccion } from '../kit'
import { useEditarBien, useRegistrarBien } from '../../queries/useInventario'
import { Bien, DatosBien, EstadoFisico, TipoBien } from '../../types/inventario'
import { mensajeDeError } from '../../utils/errores'
import { ESTADO_FISICO, TIPO, tipoDeNumero } from '../../utils/inventario'

type Formulario = Required<DatosBien>

const VACIO: Formulario = {
  numero: '', tipo: '', descripcion: '', marca: '', modelo: '', serie: '', estado_fisico: '', etiqueta_pendiente: false, observacion: '',
}
const TIPOS_DECLARABLES: TipoBien[] = ['INVENTARIABLE', 'NO_INVENTARIABLE', 'OTRO']

/**
 * Alta (con `recinto`: el bien queda en esa oficina) o edición (con `bien`) de un bien.
 * Con N°, el tipo sale del N° y el formulario lo avisa; sin N°, se elige.
 */
export function DialogoBien({
  recinto, bien, inicial, onCerrar,
}: {
  recinto?: string
  bien?: Bien
  inicial?: DatosBien
  onCerrar: () => void
}) {
  const [f, setF] = useState<Formulario>(bien ? { ...VACIO, ...pick(bien) } : { ...VACIO, ...inicial })
  const [motivo, setMotivo] = useState('')
  const registrar = useRegistrarBien()
  const editar = useEditarBien()
  const m = bien ? editar : registrar
  const tipo = tipoDeNumero(f.numero)
  const cambiar = <K extends keyof Formulario>(campo: K, valor: Formulario[K]) => setF((prev) => ({ ...prev, [campo]: valor }))

  const guardar = (e: FormEvent) => {
    e.preventDefault()
    // Con N°, el tipo lo deduce el servidor: no se manda el elegido antes.
    const datos: DatosBien = f.numero.trim() ? { ...f, tipo: undefined } : f
    if (bien) {
      const original = pick(bien)
      const cambios = Object.fromEntries(
        Object.entries(datos).filter(([c, v]) => v !== undefined && original[c as keyof Formulario] !== v),
      ) as DatosBien
      if (!Object.keys(cambios).length) return onCerrar()
      editar.mutate({ id: bien.id, datos: { ...cambios, motivo } }, { onSuccess: onCerrar })
    } else if (recinto) {
      registrar.mutate({ recinto, datos }, { onSuccess: onCerrar })
    }
  }

  return (
    <Dialogo abierto onCerrar={onCerrar} titulo={bien ? `Editar ${bien.numero || 'bien sin N°'}` : 'Agregar bien a esta oficina'}>
      <form className="pila" onSubmit={guardar}>
        <div className="formulario-grilla">
          <Campo
            etiqueta="N° de bien"
            error={tipo === null ? 'Formato desconocido: 12345-1, 971 o DCC-0001.' : null}
            ayuda={tipo ? `Tipo: ${TIPO[tipo].texto}` : 'Vacío si todavía no tiene etiqueta.'}
          >
            <Entrada value={f.numero} onChange={(e) => cambiar('numero', e.target.value)} autoCapitalize="characters" autoComplete="off" />
          </Campo>
          <Campo etiqueta="N° de serie">
            <Entrada value={f.serie} onChange={(e) => cambiar('serie', e.target.value)} autoCapitalize="characters" autoComplete="off" />
          </Campo>
        </div>
        {tipo === '' && (
          <Campo etiqueta="Tipo" ayuda="Sin N°, el tipo se elige. Cuando tenga N°, sale del N°.">
            <Seleccion value={f.tipo} onChange={(e) => cambiar('tipo', e.target.value as TipoBien)}>
              <option value="">Sin tipo</option>
              {TIPOS_DECLARABLES.map((t) => (
                <option key={t} value={t}>{TIPO[t].texto}</option>
              ))}
            </Seleccion>
          </Campo>
        )}
        <Campo etiqueta="Descripción" requerido>
          <Entrada value={f.descripcion} onChange={(e) => cambiar('descripcion', e.target.value)} required data-autofocus={!bien || undefined} />
        </Campo>
        <div className="formulario-grilla">
          <Campo etiqueta="Marca">
            <Entrada value={f.marca} onChange={(e) => cambiar('marca', e.target.value)} />
          </Campo>
          <Campo etiqueta="Modelo">
            <Entrada value={f.modelo} onChange={(e) => cambiar('modelo', e.target.value)} />
          </Campo>
        </div>
        <Campo etiqueta="Estado físico">
          <Seleccion value={f.estado_fisico} onChange={(e) => cambiar('estado_fisico', e.target.value as EstadoFisico)}>
            {(Object.keys(ESTADO_FISICO) as EstadoFisico[]).map((clave) => (
              <option key={clave} value={clave}>{ESTADO_FISICO[clave].texto}</option>
            ))}
          </Seleccion>
        </Campo>
        <Casilla etiqueta="Falta pegarle la etiqueta" checked={f.etiqueta_pendiente} onChange={(e) => cambiar('etiqueta_pendiente', e.target.checked)} />
        <Campo etiqueta="Observación">
          <AreaTexto rows={2} value={f.observacion} onChange={(e) => cambiar('observacion', e.target.value)} />
        </Campo>
        {bien && (
          <Campo etiqueta="Motivo del cambio" ayuda="Opcional. Queda en el historial.">
            <Entrada value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </Campo>
        )}
        {m.isError && <Alerta tipo="error">{mensajeDeError(m.error)}</Alerta>}
        <div className="acciones-formulario">
          <Boton variante="text" onClick={onCerrar}>Cancelar</Boton>
          <Boton type="submit" cargando={m.isPending} disabled={tipo === null}>{bien ? 'Guardar' : 'Agregar'}</Boton>
        </div>
      </form>
    </Dialogo>
  )
}

function pick(b: Bien): Formulario {
  return {
    numero: b.numero, tipo: b.tipo, descripcion: b.descripcion, marca: b.marca, modelo: b.modelo, serie: b.serie,
    estado_fisico: b.estado_fisico, etiqueta_pendiente: b.etiqueta_pendiente, observacion: b.observacion,
  }
}
